import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { getCurrentUser } from './store/authSlice';
import { ThemeProvider } from './context/ThemeContext';
import { ThemeInitializer } from './components/shared/ThemeInitializer';
import AuthPage from './pages/AuthPage';
import VerifyAccount from './components/VerifyAccount';
import VerifyLogin from './components/VerifyLogin';
import ServerDown from './pages/ServerDown';
import ServerStartPage from './pages/ServerStartPage';
import StudyPage from './pages/StudyPage';
import ResearchDashboard from './pages/ResearchDashboard';
import ResearchAdministration from './pages/ResearchAdministration';
import NotFoundPage from './pages/NotFoundPage';
import { useTranslation } from 'react-i18next';

const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const location = useLocation();
  return isAuthenticated ? element : <Navigate to="/login" state={{ from: location }} replace />;
};

const AuthWrapper: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initApp = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          await dispatch(getCurrentUser()).unwrap();
        } catch (error) {
          // A wake/transport failure is not proof that stored credentials are
          // invalid. Clear them only when the API actually rejects auth.
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 401 || status === 403) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
          }
        }
      }
      setIsInitialized(true);
    };

    initApp();
  }, [dispatch]);

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary-50 dark:bg-secondary-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600 dark:text-secondary-300">{t('app.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <ThemeInitializer />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'var(--secondary-800)',
            color: 'var(--toast-foreground)',
            borderRadius: '0.5rem',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: 'var(--success-500)',
              secondary: 'var(--toast-foreground)'
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: 'var(--danger-500)',
              secondary: 'var(--toast-foreground)'
            },
          },
        }}
      />
      <Routes>
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/investigacion" replace /> : <AuthPage key="login" />}
        />
        <Route
          path="/signup"
          element={<Navigate to="/login" replace />}
        />
        <Route
          path="/verify"
          element={isAuthenticated ? <Navigate to="/investigacion" replace /> : <VerifyAccount key="verify" />}
        />
        <Route
          path="/verify-login"
          element={isAuthenticated ? <Navigate to="/investigacion" replace /> : <VerifyLogin key="verify-login" />}
        />

        <Route path="/investigacion/administracion" element={<PrivateRoute element={<ResearchAdministration />} />} />
        <Route path="/investigacion" element={<PrivateRoute element={<ResearchDashboard />} />} />
        <Route path="/dashboard" element={<Navigate to="/investigacion" replace />} />

        <Route path="/server-down" element={<ServerDown key="server-down" />} />
        <Route path="/start-server" element={<ServerStartPage key="start-server" />} />

        <Route path="/" element={<StudyPage />} />
        <Route path="/estudio" element={<StudyPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <div className="App">
          <AuthWrapper />
        </div>
      </ThemeProvider>
    </Provider>
  );
};

export default App;
